/**
 * The "Licensing" box's rows: one per creator (and campaign) in the video, read from GET /v1/videos/:id/rights.
 * A creator clip runs in ads only when Beatly and the brand both confirmed its licence and today is within it; the
 * API decides (`ok`), this only says where each creator stands and what the person can do about it.
 */
import { clipRightsText, creatorLabelOf, type ClipLicence, type ClipRights, type Licence, type VideoRights } from './videoClient';

export type LicenceRowState = 'cleared' | 'confirm' | 'waiting' | 'ended' | 'blocked';

export interface LicenceRow {
  key: string;
  campaignId: string | null;
  creatorKey: string | null;
  label: string;
  clips: number;
  state: LicenceRowState;
  until: string | null;
  /** Beatly was asked to confirm (the API keeps one ask a day), and when, if the answer says. */
  requested: boolean;
  requestedAt: string | null;
  /** Plain words for a row that is not cleared. */
  reason: string | null;
}

/** Today as YYYY-MM-DD in the person's own calendar. */
export function todayIso(now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

export function licenceCleared(l: ClipLicence | null | undefined, today = todayIso()): boolean {
  return !!l && l.beatly && l.brand && (l.until === null || today <= l.until);
}

/** One row per creator and campaign, in the order the clips come. */
export function licenceRows(clips: ClipRights[], today = todayIso()): LicenceRow[] {
  const groups = new Map<string, ClipRights[]>();
  for (const c of clips) {
    const key = `${c.campaignId ?? ''}|${c.creatorKey ?? c.creator ?? c.mediaId}`;
    groups.set(key, [...(groups.get(key) || []), c]);
  }
  return [...groups].map(([key, group]) => {
    const first = group[0];
    const l = group.find((c) => c.licence)?.licence ?? null;
    const bad = group.find((c) => !c.ok);
    const label = creatorLabelOf(first) || 'A creator clip';
    const row = { key, campaignId: first.campaignId, creatorKey: first.creatorKey, label, clips: group.length, until: l?.until ?? null, requested: !!l?.requested, requestedAt: l?.requestedAt ?? null };
    const state = rowState(group, l, today);
    return { ...row, state, reason: state === 'cleared' ? null : bad ? clipRightsText(bad) : clipRightsText({ ...first, reason: null, licence: l }) };
  });
}

const WAITING_REASON = /^Waiting for Beatly\b/i;

function rowState(group: ClipRights[], l: ClipLicence | null, today: string): LicenceRowState {
  const first = group[0];
  // Not a clip the licence can be confirmed for (not in the Content Hub, an odd link): the API's reason says why.
  if (!first.campaignId || !first.creatorKey) return 'blocked';
  // The API's `ok` wins for a confirmed licence (its calendar decides the last day).
  if (l?.beatly && l.brand && group.every((c) => c.ok)) return 'cleared';
  // No licence state also comes back when the caller's login cannot open the clip's campaign: only the API's
  // waiting reason (or none) means Beatly is the one to act; anything else is said as the API says it.
  if (!l) return group.every((c) => c.ok || !c.reason || WAITING_REASON.test(c.reason)) ? 'waiting' : 'blocked';
  if (!l.beatly) return 'waiting';
  if (l.until && today > l.until) return 'ended';
  if (!l.brand) return 'confirm';
  // Both confirmed and in date, yet a clip is refused for something else: say what.
  return 'blocked';
}

export const allLicensed = (rows: LicenceRow[]) => rows.every((r) => r.state === 'cleared');

/** The API takes one ask a day per licence (licences.ts REQUEST_EVERY_MS); it answers an earlier ask unchanged. */
export const ASK_EVERY_MS = 24 * 3600_000;

/**
 * Whether the person may ask Beatly (again): never asked, asked more than a day ago, or asked at a time the answer
 * did not give (the API then answers with the time of the last ask, and the row shows it).
 */
export function canAskBeatly(row: Pick<LicenceRow, 'state' | 'requested' | 'requestedAt'>, now = Date.now()): boolean {
  // An ended licence can be asked about too: Beatly renews it (the API keeps that ask in its waiting list).
  if (row.state !== 'waiting' && row.state !== 'ended') return false;
  if (!row.requested || !row.requestedAt) return true;
  const at = Date.parse(row.requestedAt);
  return Number.isNaN(at) || now - at >= ASK_EVERY_MS;
}

/** A refused export (409 rights) and when it was refused. */
export interface Refusal { clips: ClipRights[]; at: number }

/** The refusal's clips while no rights answer came after it; a newer answer says where the licences stand now. */
export function liveRefusal(refusal: Refusal | null, rightsAt: number): ClipRights[] | null {
  return refusal && !(rightsAt > refusal.at) ? refusal.clips : null;
}

/** The clips a refused export (409 rights) named, over the clips the rights answer gave. */
export function mergeRefused(clips: ClipRights[], refused: ClipRights[] | null): ClipRights[] {
  if (!refused?.length) return clips;
  const byId = new Map(refused.map((c) => [c.mediaId, c]));
  return [...clips.map((c) => byId.get(c.mediaId) ?? c), ...refused.filter((c) => !clips.some((x) => x.mediaId === c.mediaId))];
}

/**
 * The clips after a licence call answered: their licence becomes the record's, and a clip that was held back only by
 * its licence runs once it is cleared. The next rights answer replaces this.
 */
export function patchLicence(clips: ClipRights[], licence: Licence, today = todayIso()): ClipRights[] {
  const next: ClipLicence = { beatly: !!licence.beatly, brand: !!licence.brand, until: licence.until, requested: !!licence.requestedAt, requestedAt: licence.requestedAt };
  const cleared = licenceCleared(next, today);
  return clips.map((c) => {
    if (c.campaignId !== licence.campaignId || c.creatorKey !== licence.creatorKey) return c;
    const ok = cleared && (c.ok || !licenceCleared(c.licence, today));
    // A licence reason from the last answer no longer holds; any other reason stays until the API answers again.
    return { ...c, licence: next, ok, reason: ok ? null : cleared ? c.reason : null };
  });
}

/** The same, on a cached rights answer. */
export function patchRights(prev: VideoRights | undefined, licence: Licence): VideoRights | undefined {
  if (!prev) return prev;
  const clips = patchLicence(prev.clips, licence);
  return { clips, ok: clips.every((c) => c.ok) };
}
